/**
 * Every figure a document prints, worked out by code (proposal.md 3.5 and 3.6,
 * decisions 16, 17 and 19 of docs/plans/report-templates.md).
 *
 * All arithmetic is in integer minor units (paise) held as `bigint`, from the
 * decimal strings an entry stores. No float touches an amount. `round` is half
 * up to one paisa (half away from zero for a negative amount).
 *
 * Per line, by `priceBasis` and the split:
 *
 * | Case | Rule |
 * |---|---|
 * | exclusive, intra | taxable = qty x rate - discount (or `line.taxable`); half = round(taxable x r / 200); tax = 2 x half |
 * | exclusive, inter | IGST = round(taxable x r / 100) |
 * | inclusive, intra | gross = `line.amount`, else qty x rate - discount; half = round(gross x r / (2 x (100 + r))); taxable = gross - 2 x half |
 * | inclusive, inter | IGST = round(gross x r / (100 + r)); taxable = gross - IGST |
 * | exclusive, single | tax = round(taxable x r / 100) |
 * | inclusive, single | tax = round(gross x r / (100 + r)); taxable = gross - tax |
 *
 * `single` is one tax at the line's rate, with input credit: VAT in the UAE,
 * Saudi Arabia, the UK and the EU, GST in Australia, Singapore and New
 * Zealand (L43 of docs/plans/ledger.md). It is held in `single`, and the
 * Indian heads stay zero.
 *
 * So the two halves of an intra-state tax are always equal, and taxable plus
 * tax is always what the customer pays. Tax is worked out per line, then
 * summed. The grand total is rounded by `rounding` and the difference prints
 * as Round off.
 *
 * The bill discount (D16) is shared across the lines in proportion to what
 * each is worth before tax (exclusive) or to its gross (inclusive), by the
 * largest-remainder rule in paise, so the parts add up to the discount
 * exactly. A charge (D17) follows the price basis, is not discounted, and is
 * taxed at its own rate: the highest rate on the lines by default.
 *
 * Off the package index, like `doc-layout.ts`.
 */
import { minorToDecimal } from './doc-format';
import { rateBasisPoints } from './gst-rates';

/* --------------------------------------------------------- decimals */

/** A decimal string as a scaled integer, or null when it is not a number. "12.5" gives { n: 125n, scale: 1 }. */
const parseDecimal = (text: string | number | null | undefined): { n: bigint; scale: number } | null => {
  if (text === null || text === undefined) return null;
  const raw = (typeof text === 'number' ? (Number.isFinite(text) ? String(text) : '') : text).trim().replace(/,/g, '');
  const match = /^([+-])?(\d*)(?:\.(\d*))?$/.exec(raw);
  if (match === null || ((match[2] ?? '') === '' && (match[3] ?? '') === '')) return null;
  const frac = match[3] ?? '';
  const n = BigInt(`${match[2] || '0'}${frac}`);
  return { n: match[1] === '-' ? -n : n, scale: frac.length };
};

/** n / d, rounded half away from zero. d > 0. */
export const roundDiv = (n: bigint, d: bigint): bigint => {
  if (d <= 0n) throw new Error('roundDiv: the divisor must be positive');
  const q = n / d;
  const r = n % d;
  const twice = (r < 0n ? -r : r) * 2n;
  if (twice >= d) return n < 0n ? q - 1n : q + 1n;
  return q;
};

const TEN = (k: number): bigint => 10n ** BigInt(k);

/** A decimal string in minor units of a currency with `digits` digits: "10" gives 1000n for INR. */
export const toMinor = (text: string | number | null | undefined, digits = 2): bigint | null => {
  const d = parseDecimal(text);
  if (d === null) return null;
  return d.scale <= digits ? d.n * TEN(digits - d.scale) : roundDiv(d.n, TEN(d.scale - digits));
};

/**
 * qty x price, in minor units, rounded once: both may hold any decimals, so a
 * rate finer than a paisa (0.125) is multiplied before it is rounded.
 */
const times = (qty: { n: bigint; scale: number }, price: { n: bigint; scale: number }, digits: number): bigint => {
  const n = qty.n * price.n;
  const scale = qty.scale + price.scale;
  return scale <= digits ? n * TEN(digits - scale) : roundDiv(n, TEN(scale - digits));
};

/* ----------------------------------------------------------- inputs */

export type DocPriceBasis = 'exclusive' | 'inclusive';
/** How the tax divides: CGST + SGST, CGST + UTGST, IGST, one tax (VAT, or a GST with one tax), or no tax at all (a bill of supply, regime none). */
export type DocTaxSplit = 'intra' | 'intra_ut' | 'inter' | 'single' | 'none';
export type DocRounding = 'none' | 'rupee' | 'ten_paise';

/** One line as the entry stores it: decimal strings, or null where the template binds nothing or the line is empty. */
export type DocLineIn = {
  readonly qty: string | null;
  readonly rate: string | null;
  readonly discount: string | null;
  /** `line.amount`: the gross the customer pays for the line. The input under inclusive; only checked under exclusive. */
  readonly amount: string | null;
  /** `line.taxable`, rarely bound. The input under exclusive when it is. */
  readonly taxable: string | null;
  /** `line.tax_rate`, a percentage. */
  readonly taxRate: string | null;
  readonly hsn?: string | null;
};

export type DocChargeIn = {
  readonly key: string;
  readonly amount: string | null;
  readonly gst: 'highest' | 'set';
  /** The rate the person picked for a `set` charge. */
  readonly rate: string | null;
};

export type DocMathInput = {
  readonly lines: readonly DocLineIn[];
  readonly priceBasis: DocPriceBasis;
  readonly split: DocTaxSplit;
  readonly rounding: DocRounding;
  /** `doc.discount`, the bill discount, or null. */
  readonly billDiscount: string | null;
  readonly charges: readonly DocChargeIn[];
  /** `paid`, or null. */
  readonly paid: string | null;
  /** The currency's minor digits: 2 for INR. */
  readonly digits: number;
};

/* ---------------------------------------------------------- outputs */

/** The tax on one line or charge, in minor units. Only the split that applies is non-zero. */
export type DocTaxed = {
  /** Rate in basis points: 1800 is 18%. */
  readonly rateBp: number;
  readonly taxable: number;
  readonly cgst: number;
  /** SGST, or UTGST under `intra_ut`: the other half. */
  readonly sgst: number;
  readonly utgst: number;
  readonly igst: number;
  /** The one tax under `single`. */
  readonly single: number;
  readonly tax: number;
  /** taxable + tax: what the customer pays for it. */
  readonly total: number;
};

export type DocLineOut = DocTaxed & {
  /** Before the bill discount: the taxable value (exclusive) or the gross (inclusive). */
  readonly base: number;
  /** Its share of the bill discount. */
  readonly discountShare: number;
  readonly hsn: string | null;
};

export type DocChargeOut = DocTaxed & { readonly key: string; readonly amount: number };

export type DocTaxGroup = {
  readonly rateBp: number;
  readonly hsn: string | null;
  readonly taxable: number;
  readonly cgst: number;
  readonly sgst: number;
  readonly utgst: number;
  readonly igst: number;
  readonly single: number;
  readonly tax: number;
};

export type DocMathProblem =
  | { readonly code: 'line_value_missing'; readonly line: number }
  | { readonly code: 'line_rate_missing'; readonly line: number }
  | { readonly code: 'not_a_number'; readonly what: string; readonly line: number | null }
  | { readonly code: 'charge_rate_missing'; readonly key: string }
  | { readonly code: 'discount_exceeds_lines' };

export type DocFigures = {
  readonly lines: readonly DocLineOut[];
  readonly charges: readonly DocChargeOut[];
  /** Σ line bases before the bill discount: `calc.subtotal`. */
  readonly subtotal: number;
  /** `calc.bill_discount`, as shared out (it may be less than asked when it exceeds the lines). */
  readonly billDiscount: number;
  /** Σ charge amounts as entered: `calc.charges`. */
  readonly chargesTotal: number;
  readonly taxable: number;
  readonly cgst: number;
  readonly sgst: number;
  readonly utgst: number;
  readonly igst: number;
  readonly single: number;
  readonly taxTotal: number;
  /** taxable + tax, before rounding. */
  readonly unrounded: number;
  readonly roundOff: number;
  readonly grandTotal: number;
  readonly paid: number;
  readonly balance: number;
  /** The tax summary, by rate. */
  readonly byRate: readonly DocTaxGroup[];
  /** The tax summary, by HSN and rate. */
  readonly byHsn: readonly DocTaxGroup[];
  readonly problems: readonly DocMathProblem[];
};

/* ------------------------------------------------------------- math */

type Taxed = { rateBp: number; taxable: bigint; cgst: bigint; sgst: bigint; utgst: bigint; igst: bigint; single: bigint; tax: bigint };

/** The tax on one amount: `value` is the taxable value (exclusive) or the gross (inclusive). */
const taxOf = (value: bigint, rateBp: number, basis: DocPriceBasis, split: DocTaxSplit): Taxed => {
  const r = BigInt(rateBp);
  const zero = { rateBp, cgst: 0n, sgst: 0n, utgst: 0n, igst: 0n, single: 0n };
  if (split === 'none' || rateBp === 0) return { ...zero, taxable: value, tax: 0n };
  if (split === 'single') {
    const single = basis === 'exclusive' ? roundDiv(value * r, 10_000n) : roundDiv(value * r, 10_000n + r);
    return { ...zero, single, tax: single, taxable: basis === 'exclusive' ? value : value - single };
  }
  if (split === 'inter') {
    const igst = basis === 'exclusive' ? roundDiv(value * r, 10_000n) : roundDiv(value * r, 10_000n + r);
    return { ...zero, igst, tax: igst, taxable: basis === 'exclusive' ? value : value - igst };
  }
  const half = basis === 'exclusive' ? roundDiv(value * r, 20_000n) : roundDiv(value * r, 2n * (10_000n + r));
  const other = split === 'intra_ut' ? { utgst: half } : { sgst: half };
  return { ...zero, ...other, cgst: half, tax: 2n * half, taxable: basis === 'exclusive' ? value : value - 2n * half };
};

/**
 * `total` shared across `weights` in proportion, by the largest-remainder
 * rule: each takes the floor of its exact share, and the paise left over go
 * one each to the largest remainders (the earlier line on a tie). The parts
 * add up to `total` exactly. Weights are non-negative; all zero shares nothing.
 */
export const shareByLargestRemainder = (total: bigint, weights: readonly bigint[]): bigint[] => {
  const sum = weights.reduce((a, w) => a + (w > 0n ? w : 0n), 0n);
  if (sum === 0n || total === 0n) return weights.map(() => 0n);
  const sign = total < 0n ? -1n : 1n;
  const abs = total * sign;
  const parts = weights.map((w) => (w > 0n ? (abs * w) / sum : 0n));
  const rems = weights.map((w, i) => ({ i, rem: w > 0n ? (abs * w) % sum : -1n }));
  let left = abs - parts.reduce((a, p) => a + p, 0n);
  rems.sort((a, b) => (a.rem === b.rem ? a.i - b.i : a.rem > b.rem ? -1 : 1));
  for (const { i, rem } of rems) {
    if (left === 0n) break;
    if (rem < 0n) continue;
    parts[i] = parts[i]! + 1n;
    left -= 1n;
  }
  return parts.map((p) => p * sign);
};

const roundingUnit = (rounding: DocRounding, digits: number): bigint | null => {
  if (rounding === 'rupee') return TEN(digits);
  if (rounding === 'ten_paise') return digits >= 1 ? TEN(digits - 1) : null;
  return null;
};

const num = (v: bigint): number => Number(v);

/** Every figure of one document. Pure; the same input always gives the same paise. */
export const computeDocument = (input: DocMathInput): DocFigures => {
  const { digits, priceBasis, split } = input;
  const problems: DocMathProblem[] = [];
  const money = (text: string | null, what: string, line: number | null): bigint | null => {
    if (text === null || text.trim() === '') return null;
    const v = toMinor(text, digits);
    if (v === null) problems.push({ code: 'not_a_number', what, line });
    return v;
  };

  // 1. Each line's base: the taxable value (exclusive) or the gross (inclusive), before the bill discount.
  const bases = input.lines.map((line, i): bigint => {
    const bound = priceBasis === 'exclusive' ? money(line.taxable, 'line.taxable', i) : money(line.amount, 'line.amount', i);
    if (bound !== null) return bound;
    const qty = line.qty === null || line.qty.trim() === '' ? null : parseDecimal(line.qty);
    if (line.qty !== null && line.qty.trim() !== '' && qty === null) problems.push({ code: 'not_a_number', what: 'line.qty', line: i });
    const rate = money(line.rate, 'line.rate', i) === null ? null : parseDecimal(line.rate);
    const discount = money(line.discount, 'line.discount', i) ?? 0n;
    if (rate === null) {
      // With no tax, a line's amount is its value: there is no before or after
      // a tax to tell apart (a payment line binds only its Paid). Under GST a
      // bound gross alone cannot say what was before tax; the line has no value.
      const gross = input.split === 'none' ? money(line.amount, 'line.amount', i) : null;
      if (gross !== null) return gross;
      problems.push({ code: 'line_value_missing', line: i });
      return 0n;
    }
    return times(qty ?? { n: 1n, scale: 0 }, rate, digits) - discount;
  });
  const rates = input.lines.map((line, i): number => {
    if (split === 'none') return 0;
    const bp = rateBasisPoints(line.taxRate);
    if (bp === null) {
      if (line.taxRate !== null && line.taxRate.trim() !== '') problems.push({ code: 'not_a_number', what: 'line.tax_rate', line: i });
      else problems.push({ code: 'line_rate_missing', line: i });
      return 0;
    }
    return bp;
  });

  // 2. The bill discount, shared by largest remainder.
  const subtotal = bases.reduce((a, b) => a + b, 0n);
  let discount = money(input.billDiscount, 'doc.discount', null) ?? 0n;
  const positive = bases.reduce((a, b) => a + (b > 0n ? b : 0n), 0n);
  if (discount > positive) {
    problems.push({ code: 'discount_exceeds_lines' });
    discount = positive;
  }
  const shares = shareByLargestRemainder(discount, bases);

  // 3. Tax per line.
  const lines: DocLineOut[] = input.lines.map((line, i) => {
    const t = taxOf(bases[i]! - shares[i]!, rates[i]!, priceBasis, split);
    return {
      rateBp: t.rateBp,
      base: num(bases[i]!),
      discountShare: num(shares[i]!),
      taxable: num(t.taxable),
      cgst: num(t.cgst),
      sgst: num(t.sgst),
      utgst: num(t.utgst),
      igst: num(t.igst),
      single: num(t.single),
      tax: num(t.tax),
      total: num(t.taxable + t.tax),
      hsn: line.hsn ?? null,
    };
  });

  // 4. Charges, at the highest line rate unless the person set one.
  const highest = rates.reduce((a, b) => Math.max(a, b), 0);
  let chargesTotal = 0n;
  const charges: DocChargeOut[] = input.charges.map((c) => {
    const amount = money(c.amount, c.key, null) ?? 0n;
    chargesTotal += amount;
    let bp = highest;
    if (split === 'none') bp = 0;
    else if (c.gst === 'set') {
      const set = rateBasisPoints(c.rate);
      if (set === null) problems.push({ code: 'charge_rate_missing', key: c.key });
      bp = set ?? highest;
    }
    const t = taxOf(amount, bp, priceBasis, split);
    return {
      key: c.key,
      amount: num(amount),
      rateBp: t.rateBp,
      taxable: num(t.taxable),
      cgst: num(t.cgst),
      sgst: num(t.sgst),
      utgst: num(t.utgst),
      igst: num(t.igst),
      single: num(t.single),
      tax: num(t.tax),
      total: num(t.taxable + t.tax),
    };
  });

  // 5. Totals, rounding, paid and balance.
  const all: readonly DocTaxed[] = [...lines, ...charges];
  const sum = (pick: (t: DocTaxed) => number): bigint => all.reduce((a, t) => a + BigInt(pick(t)), 0n);
  const taxable = sum((t) => t.taxable);
  const cgst = sum((t) => t.cgst);
  const sgst = sum((t) => t.sgst);
  const utgst = sum((t) => t.utgst);
  const igst = sum((t) => t.igst);
  const single = sum((t) => t.single);
  const taxTotal = sum((t) => t.tax);
  const unrounded = taxable + taxTotal;
  const unit = roundingUnit(input.rounding, digits);
  const grand = unit === null ? unrounded : roundDiv(unrounded, unit) * unit;
  const paid = money(input.paid, 'paid', null) ?? 0n;

  const group = (keyOf: (t: DocTaxed, i: number) => { rateBp: number; hsn: string | null }): DocTaxGroup[] => {
    const groups = new Map<string, { rateBp: number; hsn: string | null; taxable: bigint; cgst: bigint; sgst: bigint; utgst: bigint; igst: bigint; single: bigint; tax: bigint }>();
    all.forEach((t, i) => {
      const k = keyOf(t, i);
      const id = `${k.hsn ?? ''}|${k.rateBp}`;
      const g = groups.get(id) ?? { ...k, taxable: 0n, cgst: 0n, sgst: 0n, utgst: 0n, igst: 0n, single: 0n, tax: 0n };
      g.taxable += BigInt(t.taxable);
      g.cgst += BigInt(t.cgst);
      g.sgst += BigInt(t.sgst);
      g.utgst += BigInt(t.utgst);
      g.igst += BigInt(t.igst);
      g.single += BigInt(t.single);
      g.tax += BigInt(t.tax);
      groups.set(id, g);
    });
    return [...groups.values()]
      .sort((a, b) => a.rateBp - b.rateBp || (a.hsn ?? '').localeCompare(b.hsn ?? ''))
      .map((g) => ({ ...g, taxable: num(g.taxable), cgst: num(g.cgst), sgst: num(g.sgst), utgst: num(g.utgst), igst: num(g.igst), single: num(g.single), tax: num(g.tax) }));
  };

  return {
    lines,
    charges,
    subtotal: num(subtotal),
    billDiscount: num(discount),
    chargesTotal: num(chargesTotal),
    taxable: num(taxable),
    cgst: num(cgst),
    sgst: num(sgst),
    utgst: num(utgst),
    igst: num(igst),
    single: num(single),
    taxTotal: num(taxTotal),
    unrounded: num(unrounded),
    roundOff: num(grand - unrounded),
    grandTotal: num(grand),
    paid: num(paid),
    balance: num(grand - paid),
    byRate: group((t) => ({ rateBp: t.rateBp, hsn: null })),
    byHsn: group((t, i) => ({ rateBp: t.rateBp, hsn: i < lines.length ? lines[i]!.hsn : null })),
    problems,
  };
};

/* ------------------------------------------------- the total check */

export type DocTotalWord = 'match' | 'rounding' | 'mismatch' | 'not_checked';
export type DocTotalCause = 'price_basis' | 'discount_missing' | 'line_amounts' | 'unknown' | null;

/**
 * D16's `totalCheck`. The figures are for the person's sheet only: the model
 * sees the word and the cause, never a figure.
 */
export type DocTotalCheck = {
  readonly word: DocTotalWord;
  /** The entry's stored total, in minor units, or null when the template binds none. */
  readonly stored: number | null;
  readonly computed: number;
  readonly cause: DocTotalCause;
  /** The bill discount, in minor units, that makes the grand total equal the stored total; null when none does. */
  readonly discountToMatch: number | null;
};

/**
 * Compares the entry's stored total (the `total` role) with the computed one.
 *
 * - `match`: equal, and each stored line amount equals its computed line total.
 * - `rounding`: the stored total is the unrounded grand total, within one
 *   paisa per line, and each stored line amount is within one paisa of its
 *   computed line total. Issue is allowed.
 * - `mismatch`: anything else. Issue is refused. The cause is
 *   `price_basis` when the other basis would match, `discount_missing` when
 *   the stored total is lower and a bill discount would match it, else
 *   `line_amounts` or `unknown`.
 */
export const checkDocTotal = (input: DocMathInput, storedTotal: string | null, storedLineAmounts: readonly (string | null)[] = []): DocTotalCheck => {
  const figures = computeDocument(input);
  const stored = toMinor(storedTotal, input.digits);
  if (stored === null) return { word: 'not_checked', stored: null, computed: figures.grandTotal, cause: null, discountToMatch: null };
  const s = num(stored);
  const tolerance = Math.max(1, input.lines.length);
  // A line's own amount is before the bill discount (it is the bill's, not the
  // line's), so it may equal the line's total before its share or after it.
  const billMinor = toMinor(input.billDiscount, input.digits);
  const beforeBill = billMinor !== null && billMinor !== 0n ? computeDocument({ ...input, billDiscount: null }) : figures;
  // A line's amount within one paisa of its total is rounding: under intra the
  // tax is two equal rounded halves, so it can be a paisa off the exact gross.
  const linesWithin = (paise: number): boolean =>
    storedLineAmounts.every((text, i) => {
      const v = toMinor(text, input.digits);
      if (v === null || figures.lines[i] === undefined) return true;
      const off = (total: number | undefined) => total !== undefined && Math.abs(num(v) - total) <= paise;
      return off(figures.lines[i]!.total) || off(beforeBill.lines[i]?.total);
    });
  const linesAgree = linesWithin(0);
  const linesNear = linesAgree || linesWithin(1);
  if (s === figures.grandTotal && linesAgree) return { word: 'match', stored: s, computed: figures.grandTotal, cause: null, discountToMatch: null };
  if ((s === figures.grandTotal || Math.abs(s - figures.unrounded) <= tolerance) && linesNear) {
    return { word: 'rounding', stored: s, computed: figures.grandTotal, cause: null, discountToMatch: null };
  }

  const other: DocMathInput = { ...input, priceBasis: input.priceBasis === 'exclusive' ? 'inclusive' : 'exclusive' };
  const otherFigures = computeDocument(other);
  const otherMatches = s === otherFigures.grandTotal || Math.abs(s - otherFigures.unrounded) <= tolerance;
  const discountToMatch = s < figures.grandTotal ? solveDiscount(input, s) : null;
  const cause: DocTotalCause = otherMatches
    ? 'price_basis'
    : discountToMatch !== null && (input.billDiscount === null || toMinor(input.billDiscount, input.digits) === 0n)
      ? 'discount_missing'
      : !linesAgree
        ? 'line_amounts'
        : 'unknown';
  return { word: 'mismatch', stored: s, computed: figures.grandTotal, cause, discountToMatch };
};

/**
 * The bill discount, in minor units, that makes the grand total equal
 * `target`, or null. The grand total falls as the discount grows (the largest
 * remainder can move a paisa of tax between rates, so it is checked near the
 * answer), so a binary search in paise finds the smallest discount whose
 * unrounded total is at most the target; the discount that hits the target
 * exactly is preferred, then the smallest whose rounded total equals it.
 */
export const solveDiscount = (input: DocMathInput, target: number): number | null => {
  const at = (d: bigint) => computeDocument({ ...input, billDiscount: minorToDecimal(d, input.digits) });
  const zero = at(0n);
  if (target >= zero.grandTotal && target >= zero.unrounded) return null;
  const max = zero.lines.reduce((a, l) => a + BigInt(Math.max(0, l.base)), 0n);
  if (max === 0n || at(max).unrounded > target) return null;
  let lo = 0n;
  let hi = max;
  while (lo < hi) {
    const mid = (lo + hi) / 2n;
    if (at(mid).unrounded <= target) hi = mid;
    else lo = mid + 1n;
  }
  const window = 64n;
  const from = lo > window ? lo - window : 0n;
  const to = lo + window > max ? max : lo + window;
  for (let d = from; d <= to; d += 1n) if (at(d).unrounded === target) return num(d);
  for (let d = from; d <= to; d += 1n) if (at(d).grandTotal === target) return num(d);
  return null;
};

